import React, { useState, useEffect, useCallback, useMemo } from 'react'
import apiClient, { API_ENDPOINTS, BASE_URL } from '../../config/api'
import Swal from 'sweetalert2'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import usePagination from '../../hooks/usePagination'
import TablePagination from '../../Components/common/TablePagination'

const Sales = () => {
  const [filters, setFilters] = useState({
    search: '',
    dateRange: 'all',
    status: '',
    branch: ''
  })

  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedSaleId, setExpandedSaleId] = useState(null)
  const [cashierDirectory, setCashierDirectory] = useState({})
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' })
  const [branches, setBranches] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [salesReport, setSalesReport] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportError, setReportError] = useState('')
  const [filtersVisible, setFiltersVisible] = useState(true)

  const handleFilterChange = (event) => {
    const { name, value } = event.target
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }))

    if (name === 'dateRange' && value !== 'custom') {
      setCustomDateRange({ start: '', end: '' })
    }

    if (name !== 'search') {
      setExpandedSaleId(null)
    }
  }

  const handleClearFilters = () => {
    setFilters({
      search: '',
      dateRange: 'all',
      status: '',
      branch: ''
    })
    setCustomDateRange({ start: '', end: '' })
    setExpandedSaleId(null)
  }
  const handleCustomDateChange = (event) => {
    const { name, value } = event.target
    setCustomDateRange((prev) => ({
      ...prev,
      [name]: value
    }))
    setExpandedSaleId(null)
  }

  const formatCurrency = (value) => {
    const parsed = parseFloat(value)
    if (Number.isNaN(parsed)) {
      return value ?? '-'
    }
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const formatNumber = (value, { decimals = 0 } = {}) => {
    const parsed = Number(value)
    if (Number.isNaN(parsed)) {
      return '-'
    }
    return parsed.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })
  }

  const SummaryBadge = ({ icon, label, value, gradient }) => (
    <div
      className="d-flex align-items-center justify-content-between w-100 rounded-4 bg-white"
      style={{
        border: '1px solid #e2e8f0',
        padding: '0.6rem 0.85rem',
        minHeight: '56px',
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
      }}
    >
      <div className="me-3">
        <small className="text-uppercase text-muted small d-block mb-1">{label}</small>
        <div className="fw-semibold" style={{ fontSize: '0.95rem', lineHeight: 1.2 }}>
          {value ?? '-'}
        </div>
      </div>
      <div
        className="rounded-circle d-flex align-items-center justify-content-center text-white"
        style={{
          width: '34px',
          height: '34px',
          background: gradient,
          boxShadow: '0 6px 12px rgba(0,0,0,0.15)'
        }}
      >
        <i className={`bi ${icon}`} />
      </div>
    </div>
  )

  const formatDateTime = (value) => {
    if (!value) return '-'
    try {
      return new Date(value).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    } catch (error) {
      return value
    }
  }

  const getDateRangeFilter = useCallback((rangeKey, customRange) => {
    if (rangeKey === 'all' || rangeKey === '') {
      return () => true
    }

    if (rangeKey === 'custom') {
      const startDate = customRange?.start ? new Date(`${customRange.start}T00:00:00`) : null
      const endDate = customRange?.end ? new Date(`${customRange.end}T23:59:59`) : null

      if (!startDate && !endDate) {
        return () => true
      }

      return (dateString) => {
        if (!dateString) return false
        const saleDate = new Date(dateString)
        if (Number.isNaN(saleDate.valueOf())) return false
        if (startDate && saleDate < startDate) {
          return false
        }
        if (endDate && saleDate > endDate) {
          return false
        }
        return true
      }
    }

    const now = new Date()
    const start = new Date(now)

    switch (rangeKey) {
      case 'today':
        start.setHours(0, 0, 0, 0)
        break
      case '7days':
        start.setDate(now.getDate() - 7)
        break
      case '30days':
        start.setDate(now.getDate() - 30)
        break
      default:
        return () => true
    }

    return (dateString) => {
      if (!dateString) return false
      const saleDate = new Date(dateString)
      return saleDate >= start
    }
  }, [])

  const resolvedDateRange = useMemo(() => {
    const formatDate = (date) => date.toISOString().split('T')[0]
    const today = new Date()

    switch (filters.dateRange) {
      case 'today':
        return { start: formatDate(today), end: formatDate(today) }
      case '7days': {
        const start = new Date(today)
        start.setDate(start.getDate() - 6)
        return { start: formatDate(start), end: formatDate(today) }
      }
      case '30days': {
        const start = new Date(today)
        start.setDate(start.getDate() - 29)
        return { start: formatDate(start), end: formatDate(today) }
      }
      case 'custom':
        if (customDateRange.start && customDateRange.end) {
          return { start: customDateRange.start, end: customDateRange.end }
        }
        return null
      case 'all':
      default:
        return null
    }
  }, [filters.dateRange, customDateRange])

  const salesReportParams = useMemo(() => {
    if (!resolvedDateRange) {
      return null
    }

    const params = {
      start_date: resolvedDateRange.start,
      end_date: resolvedDateRange.end
    }

    const branchId = Number(filters.branch)
    if (!Number.isNaN(branchId) && branchId > 0) {
      params.branch_id = branchId
    }

    return params
  }, [resolvedDateRange, filters.branch])

  const reportAvailabilityMessage = useMemo(() => {
    if (filters.dateRange === 'custom' && (!customDateRange.start || !customDateRange.end)) {
      return 'Provide both start and end dates for the custom range.'
    }
    return ''
  }, [filters.dateRange, customDateRange])

  const handleQuickDateRange = (range) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: range
    }))
    setCustomDateRange({ start: '', end: '' })
  }

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get(API_ENDPOINTS.SALES)
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setSales(data)
    } catch (err) {
      console.error('Error fetching sales:', err)
      const message = err.response?.data?.detail || err.response?.data?.message || 'Failed to load sales. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSales()
  }, [fetchSales])

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setBranchesLoading(true)
        const response = await apiClient.get(API_ENDPOINTS.BRANCHES)
        const data = Array.isArray(response.data) ? response.data : response.data?.results || []
        setBranches(data)
      } catch (err) {
        console.warn('Unable to fetch branches:', err)
        setBranches([])
      } finally {
        setBranchesLoading(false)
      }
    }

    fetchBranches()
  }, [])

  useEffect(() => {
    if (!salesReportParams) {
      setSalesReport(null)
      setReportError('')
      setReportLoading(false)
      return
    }

    let ignore = false

    const fetchSalesReport = async () => {
      try {
        setReportLoading(true)
        setReportError('')
        const response = await apiClient.get(API_ENDPOINTS.SALES_REPORT, {
          params: {
            ...salesReportParams,
            group_by: 'day'
          }
        })
        if (!ignore) {
          setSalesReport(response.data || null)
        }
      } catch (err) {
        if (!ignore) {
          console.error('Error fetching sales report:', err)
          const message =
            err.response?.data?.detail ||
            err.response?.data?.message ||
            'Failed to load sales summary. Please try again.'
          setReportError(message)
          setSalesReport(null)
        }
      } finally {
        if (!ignore) {
          setReportLoading(false)
        }
      }
    }

    fetchSalesReport()

    return () => {
      ignore = true
    }
  }, [salesReportParams])

  const fetchCashiers = useCallback(async () => {
    try {
      const response = await apiClient.get(`${BASE_URL}/api/accounts/users/`)
      const directory = Array.isArray(response.data)
        ? response.data.reduce((acc, user) => {
            if (user?.user?.id) {
              acc[user.user.id] = {
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.user.email || `User ${user.user.id}`,
                email: user.user.email,
                code: user.code
              }
            }
            return acc
          }, {})
        : {}
      setCashierDirectory(directory)
    } catch (err) {
      console.warn('Unable to fetch cashier directory:', err)
      setCashierDirectory({})
    }
  }, [])

  useEffect(() => {
    fetchCashiers()
  }, [fetchCashiers])
  const getCashierDisplay = (sale) => {
    const info = cashierDirectory[sale.cashier]
    if (info) {
      return {
        name: info.name,
        email: info.email,
        code: info.code
      }
    }
    const fallbackName = sale.cashier_email?.split('@')?.[0] || `Cashier ${sale.cashier}`
    return {
      name: fallbackName,
      email: sale.cashier_email,
      code: null
    }
  }

  const branchOptions = useMemo(() => {
    if (Array.isArray(branches) && branches.length > 0) {
      return branches
        .filter((branch) => branch?.id)
        .map((branch) => ({
          value: branch.id.toString(),
          label: branch.name || `Branch ${branch.id}`
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    const uniqueBranches = new Set()
    sales.forEach((sale) => {
      if (sale.branch_name) {
        uniqueBranches.add(sale.branch_name)
      }
    })

    return Array.from(uniqueBranches)
      .sort()
      .map((branchName) => ({
        value: branchName,
        label: branchName
      }))
  }, [branches, sales])

  const branchIdToName = useMemo(() => {
    if (!Array.isArray(branches)) {
      return {}
    }

    return branches.reduce((acc, branch) => {
      if (branch?.id) {
        acc[branch.id] = branch.name || `Branch ${branch.id}`
      }
      return acc
    }, {})
  }, [branches])

  const selectedBranchName = useMemo(() => {
    if (!filters.branch) {
      return ''
    }

    const numericId = Number(filters.branch)
    if (!Number.isNaN(numericId) && branchIdToName[numericId]) {
      return branchIdToName[numericId]
    }

    const matchingOption = branchOptions.find((option) => option.value === filters.branch)
    return matchingOption?.label || ''
  }, [filters.branch, branchIdToName, branchOptions])

  const filteredSales = useMemo(() => {
    const matchesDate = getDateRangeFilter(filters.dateRange, customDateRange)

    return sales.filter((sale) => {
      const searchTerm = filters.search.trim().toLowerCase()
      if (searchTerm) {
        const haystack = [
          sale.sale_number,
          sale.branch_name,
          sale.cashier_email,
          sale.type_of_payment,
          sale.notes
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        if (!haystack.includes(searchTerm)) {
          return false
        }
      }

      if (filters.status && sale.status?.toLowerCase() !== filters.status.toLowerCase()) {
        return false
      }

      if (filters.branch) {
        const matchesById =
          sale.branch?.toString() === filters.branch ||
          sale.branch_id?.toString() === filters.branch ||
          sale.branchId?.toString() === filters.branch

        const matchesByName = selectedBranchName
          ? sale.branch_name === selectedBranchName
          : sale.branch_name === filters.branch

        if (!matchesById && !matchesByName) {
          return false
        }
      }

      if (!matchesDate(sale.created_at)) {
        return false
      }

      return true
    })
  }, [sales, filters, customDateRange, getDateRangeFilter, selectedBranchName])

  const {
    paginatedItems: paginatedSales,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    totalPages,
    startIndex,
    endIndex
  } = usePagination(filteredSales)

  const toggleExpand = (saleId) => {
    setExpandedSaleId((prev) => (prev === saleId ? null : saleId))
  }

  const toggleFiltersVisible = () => {
    setFiltersVisible((prev) => !prev)
  }

  const generatePDF = () => {
    if (filteredSales.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'There are no sales to export. Please adjust your filters.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      const doc = new jsPDF('portrait', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 14
      let yPos = margin
      
      // Add title
      doc.setFontSize(18)
      doc.setTextColor(40, 40, 40)
      doc.text('Sales Report', margin, yPos)
      yPos += 8
      
      // Add filters information
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      const filtersApplied = []
      if (filters.search) filtersApplied.push(`Search: ${filters.search}`)
      if (filters.status) filtersApplied.push(`Status: ${filters.status}`)
      if (filters.branch) filtersApplied.push(`Branch: ${selectedBranchName || filters.branch}`)
      if (filters.dateRange !== 'all') {
        if (filters.dateRange === 'custom') {
          filtersApplied.push(`Date Range: ${customDateRange.start || 'N/A'} to ${customDateRange.end || 'N/A'}`)
        } else {
          filtersApplied.push(`Date Range: ${filters.dateRange}`)
        }
      }
      
      if (filtersApplied.length > 0) {
        doc.text(`Filters Applied: ${filtersApplied.join(' | ')}`, margin, yPos)
        yPos += 5
      }
      
      doc.text(`Generated: ${new Date().toLocaleString()} | Total Records: ${filteredSales.length}`, margin, yPos)
      yPos += 10
      
      // Process each sale with its items
      filteredSales.forEach((sale, index) => {
        // Check if we need a new page
        if (yPos > pageHeight - 60) {
          doc.addPage()
          yPos = margin
        }
        
        const cashierInfo = getCashierDisplay(sale)
        
        // Sale header
        doc.setFontSize(12)
        doc.setTextColor(40, 40, 40)
        doc.setFont(undefined, 'bold')
        doc.text(`Sale #${sale.sale_number || 'N/A'}`, margin, yPos)
        yPos += 6
        
        // Sale details
        doc.setFontSize(9)
        doc.setTextColor(60, 60, 60)
        doc.setFont(undefined, 'normal')
        doc.text(`Branch: ${sale.branch_name || '-'}`, margin, yPos)
        doc.text(`Cashier: ${cashierInfo.name || '-'}`, margin + 60, yPos)
        yPos += 5
        doc.text(`Payment: ${sale.type_of_payment || '-'}`, margin, yPos)
        doc.text(`Status: ${sale.status || '-'}`, margin + 60, yPos)
        yPos += 5
        doc.text(`Date: ${formatDateTime(sale.created_at)}`, margin, yPos)
        yPos += 7
        
        // Items table
        if (sale.items && sale.items.length > 0) {
          const itemsData = sale.items.map(item => [
            `${item.product_name || '-'}${item.product_sku ? ` (${item.product_sku})` : ''}`,
            item.quantity?.toString() || '0',
            formatCurrency(item.unit_price),
            formatCurrency(item.subtotal),
            formatCurrency(item.profit)
          ])
          
          doc.autoTable({
            startY: yPos,
            head: [['Product', 'Qty', 'Unit Price', 'Subtotal', 'Profit']],
            body: itemsData,
            theme: 'striped',
            headStyles: {
              fillColor: [66, 139, 202],
              textColor: 255,
              fontStyle: 'bold',
              fontSize: 8
            },
            bodyStyles: {
              fontSize: 7
            },
            alternateRowStyles: {
              fillColor: [245, 245, 245]
            },
            margin: { top: yPos, left: margin, right: margin },
            styles: {
              overflow: 'linebreak',
              cellWidth: 'wrap'
            },
            columnStyles: {
              0: { cellWidth: 70 },
              1: { cellWidth: 20, halign: 'right' },
              2: { cellWidth: 30, halign: 'right' },
              3: { cellWidth: 30, halign: 'right' },
              4: { cellWidth: 30, halign: 'right' }
            }
          })
          
          yPos = doc.lastAutoTable.finalY + 5
        } else {
          doc.setFontSize(8)
          doc.setTextColor(120, 120, 120)
          doc.text('No items recorded', margin, yPos)
          yPos += 5
        }
        
        // Financial summary
        doc.setFontSize(8)
        doc.setTextColor(60, 60, 60)
        doc.text(`Subtotal: ${formatCurrency(sale.total_amount)}`, pageWidth - margin - 50, yPos)
        yPos += 4
        doc.text(`Discount: ${formatCurrency(sale.discount || 0)}`, pageWidth - margin - 50, yPos)
        yPos += 4
        doc.text(`Tax: ${formatCurrency(sale.tax || 0)}`, pageWidth - margin - 50, yPos)
        yPos += 4
        doc.setFont(undefined, 'bold')
        doc.text(`Net Amount: ${formatCurrency(sale.net_amount || sale.total_amount)}`, pageWidth - margin - 50, yPos)
        doc.setFont(undefined, 'normal')
        yPos += 8
        
        // Returns section if exists
        if (Array.isArray(sale.returns) && sale.returns.length > 0) {
          doc.setFontSize(9)
          doc.setTextColor(200, 0, 0)
          doc.setFont(undefined, 'bold')
          doc.text('Returns:', margin, yPos)
          yPos += 5
          
          const returnsData = sale.returns.map(returnItem => [
            returnItem.product_name || '-',
            returnItem.quantity?.toString() || '0',
            formatCurrency(returnItem.amount || 0)
          ])
          
          doc.autoTable({
            startY: yPos,
            head: [['Product', 'Qty', 'Amount']],
            body: returnsData,
            theme: 'striped',
            headStyles: {
              fillColor: [200, 0, 0],
              textColor: 255,
              fontStyle: 'bold',
              fontSize: 8
            },
            bodyStyles: {
              fontSize: 7
            },
            margin: { top: yPos, left: margin, right: margin },
            styles: {
              overflow: 'linebreak',
              cellWidth: 'wrap'
            }
          })
          
          yPos = doc.lastAutoTable.finalY + 5
          doc.setTextColor(60, 60, 60)
          doc.setFont(undefined, 'normal')
        }
        
        // Separator line between sales
        if (index < filteredSales.length - 1) {
          doc.setDrawColor(200, 200, 200)
          doc.line(margin, yPos, pageWidth - margin, yPos)
          yPos += 5
        }
      })
      
      // Save PDF
      const fileName = `sales-report-${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)
      
      Swal.fire({
        icon: 'success',
        title: 'PDF Downloaded',
        text: `Sales report has been downloaded as ${fileName}`,
        timer: 2000,
        showConfirmButton: false
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Sales</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Sales</li>
            <li className="breadcrumb-item active">Sales Overview</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                  <h5 className="mb-0">
                    <i className="bi bi-graph-up-arrow me-2" />
                    Sales
                  </h5>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-primary btn-sm" onClick={fetchSales} disabled={loading}>
                      <i className="bi bi-arrow-clockwise me-1" />
                      Refresh
                    </button>
                    <button className="btn btn-outline-danger btn-sm" onClick={generatePDF} disabled={loading || filteredSales.length === 0}>
                      <i className="bi bi-file-pdf me-1" />
                      Download PDF
                    </button>
                  </div>
                </div>

                <div className="border rounded-3 p-3 mb-4" style={{ background: '#f8fafc', border: '1px solid #e0e6ed' }}>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h5 className="mb-0">
                      <i className="bi bi-graph-up-arrow me-2" />
                      Sales Performance
                    </h5>
                    <small className="text-muted">
                      {salesReportParams
                        ? `Range: ${salesReportParams.start_date} → ${salesReportParams.end_date} • ${selectedBranchName || filters.branch || 'All branches'}`
                        : 'Select a date range to view sales performance'}
                    </small>
                  </div>

                  {!salesReportParams ? (
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handleQuickDateRange('today')}
                        >
                          <i className="bi bi-calendar-day me-1" />
                          Today
                        </button>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handleQuickDateRange('7days')}
                        >
                          <i className="bi bi-calendar-week me-1" />
                          Last 7 Days
                        </button>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handleQuickDateRange('30days')}
                        >
                          <i className="bi bi-calendar-month me-1" />
                          Last 30 Days
                        </button>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            setFilters((prev) => ({ ...prev, dateRange: 'custom' }))
                          }}
                        >
                          <i className="bi bi-calendar-range me-1" />
                          Custom Range
                        </button>
                      </div>
                      {reportAvailabilityMessage && (
                        <div className="alert alert-info mb-0" role="alert">
                          {reportAvailabilityMessage}
                        </div>
                      )}
                    </div>
                  ) : reportLoading ? (
                    <div className="d-flex align-items-center text-muted">
                      <div className="spinner-border text-primary me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Fetching sales report summary...
                    </div>
                  ) : reportError ? (
                    <div className="alert alert-danger mb-0" role="alert">
                      {reportError}
                    </div>
                  ) : salesReport ? (
                    <>
                      <div className="row row-cols-2 row-cols-md-4 g-2 g-md-3 mb-2">
                        <div className="col">
                          <SummaryBadge
                            icon="bi-receipt"
                            label="Total Orders"
                            value={formatNumber(salesReport.summary?.total_orders)}
                            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          />
                        </div>
                        <div className="col">
                          <SummaryBadge
                            icon="bi-box-seam"
                            label="Units Sold"
                            value={formatNumber(salesReport.summary?.total_units_sold, { decimals: 2 })}
                            gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                          />
                        </div>
                        <div className="col">
                          <SummaryBadge
                            icon="bi-currency-dollar"
                            label="Total Sales"
                            value={formatCurrency(salesReport.summary?.total_sales)}
                            gradient="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
                          />
                        </div>
                        <div className="col">
                          <SummaryBadge
                            icon="bi-graph-up"
                            label="Avg Order Value"
                            value={formatCurrency(salesReport.summary?.average_order_value)}
                            gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                          />
                        </div>
                      </div>

                     
                    </>
                  ) : (
                    <p className="text-muted mb-0">No summary data available for the selected filters.</p>
                  )}
                </div>

                <div className="border rounded-3 p-3 mb-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <h6 className="mb-0">Filters</h6>
                    <div className="d-flex align-items-center gap-2">
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleClearFilters}>
                        Reset
                      </button>
                      <button
                        className="btn btn-light btn-sm d-flex align-items-center gap-1"
                        type="button"
                        onClick={toggleFiltersVisible}
                        aria-expanded={filtersVisible}
                        aria-controls="sales-filters"
                      >
                        <i className={`bi bi-chevron-${filtersVisible ? 'up' : 'down'}`} />
                        {filtersVisible ? 'Hide' : 'Show'} filters
                      </button>
                    </div>
                  </div>

                  <div className={`collapse ${filtersVisible ? 'show' : ''}`} id="sales-filters">
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label">Search</label>
                        <input
                          type="text"
                          className="form-control"
                          name="search"
                          value={filters.search}
                          onChange={handleFilterChange}
                          placeholder="Invoice, customer, SKU..."
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Date Range</label>
                        <select
                          className="form-select"
                          name="dateRange"
                          value={filters.dateRange}
                          onChange={handleFilterChange}
                        >
                          <option value="all">All time</option>
                          <option value="today">Today</option>
                          <option value="7days">Last 7 days</option>
                          <option value="30days">Last 30 days</option>
                          <option value="custom">Custom range</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          name="status"
                          value={filters.status}
                          onChange={handleFilterChange}
                        >
                          <option value="">All</option>
                          <option value="completed">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Branch</label>
                        <select
                          className="form-select"
                          name="branch"
                          value={filters.branch}
                          onChange={handleFilterChange}
                        >
                          <option value="">All branches</option>
                          {branchOptions.length === 0 ? (
                            <option value="" disabled>
                              {branchesLoading ? 'Loading branches...' : 'No branches found'}
                            </option>
                          ) : (
                            branchOptions.map((branch) => (
                              <option key={branch.value} value={branch.value}>
                                {branch.label}
                              </option>
                            ))
                          )}
                        </select>
                      </div>
                      {filters.dateRange === 'custom' && (
                        <>
                          <div className="col-md-3">
                            <label className="form-label">Start date</label>
                            <input
                              type="date"
                              className="form-control"
                              name="start"
                              value={customDateRange.start}
                              max={customDateRange.end || undefined}
                              onChange={handleCustomDateChange}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">End date</label>
                            <input
                              type="date"
                              className="form-control"
                              name="end"
                              value={customDateRange.end}
                              min={customDateRange.start || undefined}
                              onChange={handleCustomDateChange}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {filters.dateRange === 'custom' && (
                      <div className="mt-2 text-muted small">
                        Select start and end dates to narrow down the results. Leaving either field empty makes the filter open-ended.
                      </div>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }} />
                        <th scope="col">Sale #</th>
                        <th scope="col">Branch</th>
                        <th scope="col">Cashier</th>
                        <th scope="col">Payment</th>
                        <th scope="col" className="text-end">Total</th>
                        <th scope="col">Status</th>
                        <th scope="col">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <div className="spinner-border text-primary me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Fetching latest sales...
                          </td>
                        </tr>
                      )}
                      {!loading && filteredSales.length === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">
                            No sales match the current filters.
                            </td>
                          </tr>
                      )}
                      {!loading &&
                        paginatedSales.map((sale) => {
                          const isExpanded = expandedSaleId === sale.id
                          return (
                            <React.Fragment key={sale.id}>
                              <tr
                                onClick={() => toggleExpand(sale.id)}
                                style={{ cursor: 'pointer' }}
                                className={isExpanded ? 'table-active' : ''}
                              >
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      toggleExpand(sale.id)
                                    }}
                                  >
                                    <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
                                  </button>
                                </td>
                                <td className="fw-semibold">{sale.sale_number || '-'}</td>
                                <td>{sale.branch_name || '-'}</td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span className="fw-semibold">{getCashierDisplay(sale).name}</span>
                                    {getCashierDisplay(sale).email && (
                                      <small className="text-muted">{getCashierDisplay(sale).email}</small>
                                    )}
                                  </div>
                                </td>
                                <td className="text-capitalize">{sale.type_of_payment || '-'}</td>
                                <td className="text-end fw-semibold">{formatCurrency(sale.total_amount)}</td>
                                <td>
                                  <span className={`badge bg-${sale.status === 'completed' ? 'success' : sale.status === 'pending' ? 'warning' : sale.status === 'cancelled' ? 'danger' : 'secondary'}`}>
                                    {sale.status || 'Unknown'}
                                  </span>
                                </td>
                                <td>{formatDateTime(sale.created_at)}</td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-light-subtle">
                                  <td colSpan="8">
                                    <div className="p-3 border rounded-3 bg-white shadow-sm">
                                      <div className="row g-4">
                                        <div className="col-lg-4">
                                          <div className="h-100">
                                            <h6 className="text-uppercase text-muted small mb-3">Snapshot</h6>
                                            <div className="mb-3">
                                              <p className="mb-1 text-muted small">Cashier</p>
                                              <p className="mb-0 fw-semibold">{getCashierDisplay(sale).name}</p>
                                              {getCashierDisplay(sale).email && (
                                                <small className="text-muted">{getCashierDisplay(sale).email}</small>
                                              )}
                                              {getCashierDisplay(sale).code && (
                                                <small className="d-block text-muted">Code: {getCashierDisplay(sale).code}</small>
                                              )}
                                            </div>
                                            <div className="mb-3">
                                              <p className="mb-1 text-muted small">Branch</p>
                                              <p className="mb-0">{sale.branch_name || '-'}</p>
                                            </div>
                                            <div className="mb-3">
                                              <p className="mb-1 text-muted small">Payment Method</p>
                                              <span className="badge bg-light text-dark text-capitalize">{sale.type_of_payment}</span>
                                            </div>
                                            <div className="mb-3">
                                              <p className="mb-1 text-muted small">Created</p>
                                              <p className="mb-0">{formatDateTime(sale.created_at)}</p>
                                            </div>
                                            <div>
                                              <p className="mb-1 text-muted small">Financial Summary</p>
                                              <div className="border rounded-3 p-2">
                                                <div className="d-flex justify-content-between small">
                                                  <span>Subtotal</span>
                                                  <strong>{formatCurrency(sale.total_amount)}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between small">
                                                  <span>Discount</span>
                                                  <strong>{formatCurrency(sale.discount)}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between small">
                                                  <span>Tax</span>
                                                  <strong>{formatCurrency(sale.tax)}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between small">
                                                  <span>Net</span>
                                                  <strong>{formatCurrency(sale.net_amount)}</strong>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-lg-8">
                                          <h6 className="text-uppercase text-muted small mb-3">Items</h6>
                                          {sale.items?.length > 0 ? (
                                            <div className="table-responsive small">
                                              <table className="table table-sm align-middle mb-0">
                                                <thead>
                                                  <tr>
                                                    <th>Product</th>
                                                    <th className="text-end">Qty</th>
                                                    <th className="text-end">Unit</th>
                                                    <th className="text-end">Subtotal</th>
                                                    <th className="text-end">Profit</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {sale.items.map((item) => (
                                                    <tr key={item.id}>
                                                      <td>
                                                        <div className="d-flex flex-column">
                                                          <span>{item.product_name}</span>
                                                          <small className="text-muted">{item.product_sku}</small>
                                                        </div>
                                                      </td>
                                                      <td className="text-end">{item.quantity}</td>
                                                      <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                      <td className="text-end">{formatCurrency(item.subtotal)}</td>
                                                      <td className="text-end">{formatCurrency(item.profit)}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          ) : (
                                            <p className="text-muted mb-0">No line items recorded.</p>
                                          )}

                                          {Array.isArray(sale.returns) && sale.returns.length > 0 && (
                                            <div className="mt-4">
                                              <h6 className="text-uppercase text-muted small mb-2">Returns</h6>
                                              <div className="table-responsive small">
                                                <table className="table table-sm mb-0">
                                                  <thead>
                                                    <tr>
                                                      <th>Product</th>
                                                      <th className="text-end">Qty</th>
                                                      <th className="text-end">Amount</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {sale.returns.map((returnItem) => (
                                                      <tr key={returnItem.id}>
                                                        <td>{returnItem.product_name || '-'}</td>
                                                        <td className="text-end">{returnItem.quantity || '-'}</td>
                                                        <td className="text-end">{formatCurrency(returnItem.amount)}</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        })}
                    </tbody>
                  </table>
                </div>

                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  itemLabel="sales"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Sales


